import { AnimatePresence, motion } from "framer-motion";
import ChatbotUI from "./ChatbotUI";
import React, { useState, useEffect } from "react";
import chatbotlogo from "../assets/images/logos/chatbotlogo2.png"

export function ChatbotLauncher() {
  const [open, setOpen] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isNearEdge, setIsNearEdge] = useState({ left: false, right: false, top: false, bottom: false });

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  // Always start at original position on mount
  useEffect(() => {
    setPosition({ x: 0, y: 0 });
  }, []);

  const checkEdgeProximity = (x, y) => {
    const buttonSize = 56;
    const edgeThreshold = 50; // Distance to edge to consider "near edge"
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Get actual position relative to viewport
    let actualX, actualY;
    if (x === 0 && position.x === 0) {
      actualX = windowWidth - 100;
      actualY = windowHeight - 160;
    } else {
      actualX = x;
      actualY = y;
    }
    
    setIsNearEdge({
      left: actualX < edgeThreshold,
      right: actualX > windowWidth - buttonSize - edgeThreshold,
      top: actualY < edgeThreshold,
      bottom: actualY > windowHeight - buttonSize - edgeThreshold,
    });
  };

  const handleDragEnd = (event, info) => {
    const newX = position.x + info.offset.x;
    const newY = position.y + info.offset.y;
    
    const buttonSize = 56;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const snapDistance = 40;
    
    let snappedX = newX;
    let snappedY = newY;
    
    // Calculate actual position
    let actualX, actualY;
    if (newX === 0 && position.x === 0) {
      actualX = windowWidth - 100;
      actualY = windowHeight - 160;
    } else {
      actualX = newX;
      actualY = newY;
    }
    
    // Snap to edges based on proximity (but don't hide completely)
    if (actualX < snapDistance) {
      snappedX = 10; // Keep 10px from edge
    } else if (actualX > windowWidth - buttonSize - snapDistance) {
      snappedX = windowWidth - buttonSize - 10; // Keep 10px from edge
    }
    
    if (actualY < snapDistance) {
      snappedY = 10; // Keep 10px from edge
    } else if (actualY > windowHeight - buttonSize - snapDistance) {
      snappedY = windowHeight - buttonSize - 10; // Keep 10px from edge
    }
    
    // Constrain within bounds
    const constrainedX = Math.max(0, Math.min(snappedX, windowWidth - buttonSize));
    const constrainedY = Math.max(0, Math.min(snappedY, windowHeight - buttonSize));
    
    setPosition({ x: constrainedX, y: constrainedY });
    checkEdgeProximity(constrainedX, constrainedY);
    
    setTimeout(() => setIsDragging(false), 100);
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDrag = (event, info) => {
    const newX = position.x + info.offset.x;
    const newY = position.y + info.offset.y;
    checkEdgeProximity(newX, newY);
  };

  const handleOpenChat = () => {
    // Don't open if actively dragging
    if (!isDragging) {
      setOpen(true);
      setShowHint(false);
    }
  };

  const resetPosition = () => {
    setPosition({ x: 0, y: 0 });
    setIsNearEdge({ left: false, right: false, top: false, bottom: false });
  };

  // Calculate opacity based on edge proximity (visual only, doesn't affect clickability)
  const getOpacity = () => {
    if (isDragging) return 1;
    // Make slightly transparent when near edges but still clickable
    if (isNearEdge.left || isNearEdge.right || isNearEdge.top || isNearEdge.bottom) {
      return 0.75;
    }
    return 1;
  };

  // Calculate scale based on edge proximity
  const getScale = () => {
    if (isDragging) return 1;
    if (isNearEdge.left || isNearEdge.right || isNearEdge.top || isNearEdge.bottom) {
      return 0.9;
    }
    return 1;
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.div
            className="fixed z-50"
            drag
            dragMomentum={false}
            dragElastic={0}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDrag={handleDrag}
            animate={{
              x: position.x,
              y: position.y,
              opacity: getOpacity(),
              scale: getScale(),
            }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 35
            }}
            initial={false}
            style={{
              right: 20,
              bottom: 80,
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
          >
            {/* Reset Position Button */}
            <motion.button
              className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-3 py-1.5 rounded-full opacity-0 hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-auto shadow-lg"
              onClick={resetPosition}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              ↺ Reset Position
            </motion.button>

            {/* Hint Tooltip - Hide when near edges */}
            <AnimatePresence>
              {showHint && !isDragging && !isNearEdge.left && !isNearEdge.right && !isNearEdge.top && !isNearEdge.bottom && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className="absolute right-20 bottom-2 bg-white shadow-lg px-4 py-2 rounded-xl border border-green-100 text-gray-700 text-sm whitespace-nowrap font-medium pointer-events-none"
                >
                  🌿 Chat with Assistant
                  <div className="absolute right-[-6px] top-1/2 transform -translate-y-1/2 w-3 h-3 bg-white border-r border-b border-green-100 rotate-45"></div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Ripple Waves - Stop when near edges or dragging */}
            <div className="relative">
              {!isNearEdge.left && !isNearEdge.right && !isNearEdge.top && !isNearEdge.bottom && !isDragging && (
                <>
                  <motion.div
                    className="absolute inset-0 rounded-full pointer-events-none"
                    animate={{
                      scale: [1, 1.8, 2.2],
                      opacity: [0.6, 0.3, 0],
                    }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0 }}
                    style={{ border: "2px solid #22c55e", borderRadius: "50%" }}
                  />
                  
                  <motion.div
                    className="absolute inset-0 rounded-full pointer-events-none"
                    animate={{
                      scale: [1, 2, 2.5],
                      opacity: [0.4, 0.2, 0],
                    }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: "easeOut", delay: 0.5 }}
                    style={{ border: "2px solid #10b981", borderRadius: "50%" }}
                  />
                  
                  <motion.div
                    className="absolute inset-0 rounded-full pointer-events-none"
                    animate={{
                      scale: [1, 2.2, 2.8],
                      opacity: [0.3, 0.15, 0],
                    }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeOut", delay: 1 }}
                    style={{ border: "1.5px solid #059669", borderRadius: "50%" }}
                  />

                  <motion.div
                    className="absolute inset-[-8px] rounded-full pointer-events-none"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.1, 0.3],
                    }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    style={{ background: "radial-gradient(circle, rgba(34,197,94,0.2) 0%, rgba(34,197,94,0) 70%)" }}
                  />
                </>
              )}

              <motion.button
                onClick={handleOpenChat}
                whileHover={{ scale: (isNearEdge.left || isNearEdge.right || isNearEdge.top || isNearEdge.bottom) ? 1 : 1.08 }}
                whileTap={{ scale: 0.92 }}
                animate={{
                  rotate: (!isNearEdge.left && !isNearEdge.right && !isNearEdge.top && !isNearEdge.bottom && !isDragging) ? [0, 5, -5, 0] : 0,
                }}
                transition={{
                  rotate: { repeat: Infinity, duration: 3, ease: "easeInOut" },
                }}
                className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-xl hover:shadow-2xl transition-all duration-300 bg-transparent"
                aria-label="Open sustainability assistant chat"
              >
                <div className="absolute inset-0 rounded-full shadow-inner" />
                <img
                  src={chatbotlogo}
                  alt="SustainCred Logo"
                  className="w-full h-full object-contain rounded-full drop-shadow-lg pointer-events-none"
                />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chatbot UI */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed bottom-8 right-20 z-50"
          >
            <ChatbotUI open={open} setOpen={setOpen} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}